import { useState } from 'react';
import { Link } from 'react-router-dom';

const FAQS = [
  {
    category: 'Getting Started',
    items: [
      { q: 'Is PerkVora free to use?', a: 'Yes — for shoppers, PerkVora is 100% free, forever. No credit card required, no premium paywall, no "pro tier" with better deals. You get access to every coupon, cashback offer, and perk on the platform the moment you sign up.' },
      { q: 'Do I need to create an account?', a: 'You can browse all deals without an account. Creating a free account unlocks cashback tracking, personalized recommendations, loyalty points, saved favorites, and the ability to cash out your earnings.' },
      { q: 'How do I redeem a coupon?', a: 'Click any deal to see the details. For code-based offers, copy the code with one tap and paste it at checkout on the merchant\'s site. For cashback offers, activate the deal before you shop, and we\'ll track your purchase automatically.' },
      { q: 'How fresh are the deals?', a: 'We verify every deal before it goes live and re-test popular deals daily. "Last verified" timestamps appear on every coupon, so you can see exactly how recently we confirmed it still works.' },
    ]
  },
  {
    category: 'Cashback & Rewards',
    items: [
      { q: 'How does cashback work?', a: 'Browse a cashback offer on PerkVora, click "Activate Cashback," then complete your purchase on the merchant site. We track the sale with the merchant, and your cashback is credited to your PerkVora wallet (typically within 1-7 days, pending merchant confirmation).' },
      { q: 'When can I withdraw my cashback?', a: 'Once your balance hits $5, you can transfer it to your bank via ACH or debit card, powered by Stripe. Cashback enters a 30-90 day pending period to account for merchant return windows, then becomes available for withdrawal.' },
      { q: 'What are PerkVora Points?', a: 'You earn points with every redemption — 10 points per claim by default, more for featured deals. Points stack toward higher loyalty tiers (Silver → Gold → Platinum), which unlock bonus cashback rates, priority support, and exclusive member deals.' },
      { q: 'Do rewards expire?', a: 'Cashback never expires as long as your account is active. Loyalty points reset annually based on your tier anniversary to keep the program fair.' },
    ]
  },
  {
    category: 'Merchants & Brands',
    items: [
      { q: 'How are merchants vetted?', a: 'Every merchant goes through a verification process: business legitimacy check, affiliate network validation, and manual review by our team. We don\'t accept every merchant that applies — only brands that match our quality bar.' },
      { q: 'Can I list my business?', a: 'Yes. Sign up for a free merchant account, verify your business, and submit your first deal. Approved merchants typically go live within 24 hours. Check our "For Business" page for details.' },
      { q: 'Which affiliate networks do you support?', a: 'CJ Affiliate, Rakuten, Impact, ShareASale, Awin, Amazon Associates, and direct integrations. If you work with a network we don\'t support yet, reach out — we\'re always adding more.' },
    ]
  },
  {
    category: 'Privacy & Security',
    items: [
      { q: 'Do you sell my data?', a: 'No. Never. We make money from merchant commissions, not from selling your data. We\'re CCPA and GDPR compliant, and you can request deletion of your account and associated data at any time.' },
      { q: 'What data do you collect?', a: 'Only what\'s necessary to deliver the service: account info (name, email), redemption history (to track cashback), and aggregated usage data (to improve recommendations). Full details in our privacy policy.' },
      { q: 'Is my cashback secure?', a: 'Yes. All payouts go through Stripe Connect with industry-standard security. Cashback balances are held in escrow until you withdraw. We use fraud detection to prevent unauthorized access.' },
    ]
  },
  {
    category: 'Troubleshooting',
    items: [
      { q: 'A coupon code didn\'t work. What now?', a: 'First, make sure you met any minimum order or eligibility requirements. If it still doesn\'t work, tap the "Report" button on the deal page — our team investigates within a few hours and removes broken codes. You\'ll earn bonus points for helpful reports.' },
      { q: 'Why didn\'t my cashback track?', a: 'The most common reasons: (1) ad-blocker interfered with tracking, (2) you didn\'t activate the cashback before purchasing, (3) you used another coupon site\'s link in the same session. If you believe a sale should have tracked, contact support with your order confirmation and we\'ll investigate with the merchant.' },
      { q: 'I forgot my password.', a: 'No problem — use the "Forgot password" link on the login page. You\'ll receive a secure reset link at your registered email within a minute.' },
      { q: 'How do I delete my account?', a: 'Email privacy@perkvora.com from your registered address, and we\'ll process the deletion within 30 days (CCPA) or 30 days (GDPR). Pending cashback will be paid out first.' },
    ]
  },
];

export default function FAQPage() {
  const [open, setOpen] = useState({ '0-0': true });
  const [query, setQuery] = useState('');

  const toggle = (key) => setOpen(o => ({ ...o, [key]: !o[key] }));

  const q = query.trim().toLowerCase();
  const filtered = FAQS.map(g => ({
    ...g,
    items: q ? g.items.filter(i => i.q.toLowerCase().includes(q) || i.a.toLowerCase().includes(q)) : g.items,
  })).filter(g => g.items.length > 0);

  return (
    <div>
      {/* Hero */}
      <section style={{ background: 'var(--hero-gradient)', color: '#fff', padding: '96px 0 56px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-20%', left: '-10%', width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle, rgba(107,47,217,0.4) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="container-narrow" style={{ position: 'relative' }}>
          <span className="chip" style={{ marginBottom: 20 }}>🙋 Help Center</span>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 16 }}>
            Frequently asked <span className="gradient-text">questions</span>
          </h1>
          <p style={{ fontSize: 17, opacity: 0.86, maxWidth: 540, margin: '0 auto 32px', lineHeight: 1.6 }}>
            Answers to the questions we hear most. Can't find yours? Reach out anytime.
          </p>

          <div style={{ maxWidth: 520, margin: '0 auto', display: 'flex', background: '#fff', borderRadius: 999, padding: 6, boxShadow: '0 12px 32px rgba(14,15,46,0.25)' }}>
            <span style={{ display: 'flex', alignItems: 'center', padding: '0 10px 0 16px', color: '#8B94A8', fontSize: 16 }}>🔍</span>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search help articles…"
              style={{ flex: 1, padding: '12px 6px', border: 'none', outline: 'none', fontSize: 15, color: '#0F1225', background: 'transparent', minWidth: 0 }}
            />
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section" style={{ background: '#fff' }}>
        <div className="container-narrow">
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>🤔</div>
              <h3 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>No results for "{query}"</h3>
              <p style={{ fontSize: 15, color: '#4A5568' }}>Try a different search, or <Link to="/contact">message us directly</Link>.</p>
            </div>
          ) : filtered.map((group, gi) => (
            <div key={group.category} style={{ marginBottom: 40 }}>
              <h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.015em', marginBottom: 18, color: '#1A1B4B' }}>{group.category}</h2>
              {group.items.map((item, ii) => {
                const key = `${gi}-${ii}`;
                const isOpen = open[key];
                return (
                  <div key={key} className={`faq-item ${isOpen ? 'open' : ''}`}>
                    <div className="faq-q" onClick={() => toggle(key)}>
                      <span>{item.q}</span>
                      <span className="faq-toggle">+</span>
                    </div>
                    {isOpen && <div className="faq-a">{item.a}</div>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </section>

      {/* Still need help */}
      <section className="section section-tinted">
        <div className="container-narrow text-center">
          <h2 className="section-h2">Still stuck?</h2>
          <p className="section-lead">Our team replies to every question — usually within a business day.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn btn-primary btn-lg">Contact support</Link>
            <a href="mailto:hello@perkvora.com" className="btn btn-outline btn-lg">Email us</a>
          </div>
        </div>
      </section>
    </div>
  );
}
